/**
 * A client's STATE, in words, beside the name wherever a client is picked from
 * a list (Arthur, 8 October). Two clients can share a name, one living and one
 * deceased, and a reference nobody carries in their head is all that tells them
 * apart.
 *
 * THE SIX STATES, and a client is in exactly one: Pending appointment, Active,
 * Pending discharge, Discharged, Deceased, Not appointed. ACTIVE IS BLANK: blank
 * means go, a tag means this is not a client you can simply pick up and work.
 * Words, never colour alone.
 *
 * This is the one place the stored status becomes a state on screen:
 *  - AIP and IP are both Active. Temporary and permanent are not distinguished
 *    anywhere a case manager looks; the field keeps them for Compliance.
 *  - 'Pipeline' is Pending appointment: no judge has signed Allied's order, so
 *    Allied cannot act (AIP and IP in Procura are Allied's own appointment,
 *    never the client's adjudication).
 *  - 'Held at load' is shown as Pending appointment too (Arthur, 8 October): the
 *    database stores it only when there is no derivable state (no live
 *    appointment, no ending, no death) and a hold exists. It is a display
 *    fallback, never written; the hold itself is the separate marker below, and
 *    both clear when the hold is ruled on.
 *  - 'Deceased, discharged' is Deceased.
 *  - 'Not appointed' (110): Allied never held authority, on a recorded outcome.
 */
const STATE_OF: Readonly<Record<string, string | null>> = {
  Pipeline: 'Pending appointment',
  'Held at load': 'Pending appointment',
  AIP: null,
  IP: null,
  'Pending discharge': 'Pending discharge',
  Discharged: 'Discharged',
  Deceased: 'Deceased',
  'Deceased, discharged': 'Deceased',
  'Not appointed': 'Not appointed',
};

/** The state shown for a stored status, or null for Active (and for nothing known). */
export function stateTag(status: string | null | undefined): string | null {
  return status == null ? null : STATE_OF[status] ?? null;
}

export function StatusTag({ status, className = '' }: { status: string | null | undefined; className?: string }) {
  const tag = stateTag(status);
  if (!tag) return null;
  return (
    <span
      className={`shrink-0 rounded border border-neutral-300 px-1.5 py-0.5 text-xs font-semibold text-neutral-600 ${className}`}
    >
      {tag}
    </span>
  );
}

/**
 * HELD AT LOAD IS NOT A STATE (8 October). It is a fact about the RECORD: a
 * person has to rule on something before the load can finish this client. It
 * sits beside whatever state the client is in, never in place of it, and goes
 * when the hold is ruled on. So it is drawn differently from a state: a
 * rounded, dashed outline in normal weight, reading "this record has an open
 * question", not "this client is in this state". Read from the open hold
 * (`client_hold`), never from the status field.
 */
export function HeldMarker({ held, className = '' }: { held: boolean | null | undefined; className?: string }) {
  if (!held) return null;
  return (
    <span
      title="A person has to rule on this record before it is complete"
      className={`shrink-0 rounded-full border border-dashed border-amber-600 px-2 py-0.5 text-xs text-amber-900 ${className}`}
    >
      Held at load
    </span>
  );
}
